import type { WorkspaceFilterSelection } from './workflowCards.schema';

export type WorkflowCardWorkspace = {
  rowId: string;
  name: string;
  color: string;
  version?: unknown;
  workspaceCreatedBy?: string | null;
  workspaceUpdatedAt?: string | null;
};

export type WorkflowCardDocumentation = {
  url?: string | null;
  file?: { name: string; size: number; digest: string } | null;
};

export type WorkflowCardItem = {
  rowId: string;
  name: string;
  description?: string | null;
  canUpdate?: boolean | null;
  settings?: unknown;
  actions?: { nodes: { key: string; title: string }[] } | null;
  traces?: { totalCount: number } | null;
  groups?: {
    totalCount: number;
    nodes: {
      rowId: string;
      name: string;
      avatar?: string | null;
      canActQuick?: boolean | null;
    }[];
  } | null;
  documentation?: WorkflowCardDocumentation | null;
  workspace?: WorkflowCardWorkspace | null;
};

// A workspace surfaced by the dashboard filter has the same shape as the
// workspace embedded in a workflow card.
export type QualifyingWorkspace = WorkflowCardWorkspace;

export const getQualifyingWorkspaces = (
  workflows: readonly WorkflowCardItem[]
): QualifyingWorkspace[] => {
  const byId = new Map<string, QualifyingWorkspace>();
  for (const wf of workflows) {
    if (wf.workspace) {
      byId.set(wf.workspace.rowId, {
        rowId: wf.workspace.rowId,
        name: wf.workspace.name,
        color: wf.workspace.color,
        version: wf.workspace.version,
        workspaceCreatedBy: wf.workspace.workspaceCreatedBy,
        workspaceUpdatedAt: wf.workspace.workspaceUpdatedAt
      });
    }
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
};

export const matchesWorkspaceSelection = (
  workflow: WorkflowCardItem,
  selection: WorkspaceFilterSelection
): boolean => {
  if (selection.kind === 'production') {
    return !workflow.workspace;
  }
  return workflow.workspace?.rowId === selection.id;
};

const byUpdatedAtDesc = (a: QualifyingWorkspace, b: QualifyingWorkspace) =>
  (b.workspaceUpdatedAt ?? '').localeCompare(a.workspaceUpdatedAt ?? '');

// Split workspaces into the current user's and the rest, each sorted by most
// recently updated first.
export const groupWorkspaces = (
  workspaces: readonly QualifyingWorkspace[],
  userId: string
): { mine: QualifyingWorkspace[]; others: QualifyingWorkspace[] } => ({
  mine: workspaces
    .filter(w => w.workspaceCreatedBy === userId)
    .sort(byUpdatedAtDesc),
  others: workspaces
    .filter(w => w.workspaceCreatedBy !== userId)
    .sort(byUpdatedAtDesc)
});
